import {Injectable, Type} from '@angular/core';
import {Observable} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {QueryParamsApiModel} from '../../../../../api/models/query-params-api.model';
import {FilterParameterApiModel} from '../../../../../api/models/filter-parameter.api.model';
import {ReactionApiModel} from '../../../../../api/models/reaction.api.model';
import {ReactionRestService} from '../../../../../api/services/reaction.rest.service';
import {ReactionListItemInterface} from '../../../../../core/interfaces/reaction-list-item.interface';
import {SocialListAdapter, SocialListLoadResult} from '../social-list-adapter.interface';
import {SocialListModalConfig} from '../social-list-modal-config.interface';
import {SocialListType} from '../social-list-type.type';
import {ReactionListItemComponent} from '../items/reaction-list-item.component';
import {ReactionListItemsBuilderService} from '../builders/reaction-list-items-builder.service';
import {ReactionListCacheFacade} from '../reaction-list-cache.facade';

@Injectable({providedIn: 'root'})
export class LocalSubjectReactionsSocialListAdapter implements SocialListAdapter<any, ReactionApiModel, ReactionListItemInterface> {
  public readonly type: SocialListType = 'local-subject-reactions';
  public readonly itemComponent: Type<ReactionListItemComponent> = ReactionListItemComponent;
  public readonly emptyTranslationKey = 'shared.modal.reaction_list.empty';
  public readonly trimmedTranslationKey = 'shared.modal.reaction_list.trimmed';
  public readonly titleTranslationKey = 'shared.modal.modal_service.reactions.title';
  public readonly rendererInputs = {showReactionIntent: true};

  constructor(
    private readonly reactionListItemsBuilder: ReactionListItemsBuilderService,
    private readonly reactionListCacheFacade: ReactionListCacheFacade,
    private readonly reactionRS: ReactionRestService,
  ) {
  }

  public loadPage(
    config: SocialListModalConfig<any, ReactionApiModel>,
    page: number,
    pageSize: number
  ): Observable<SocialListLoadResult<ReactionListItemInterface>> {
    return this.reactionListCacheFacade.loadPage$(
      {listType: this.type, subjectId: config.subject['@id']},
      page,
      pageSize,
      this.createFetchPage(config, page, pageSize),
      config.initialData || config.subject?.reactions || []
    ).pipe(
      mergeMap(({reactions, hasMore, loadedPageCount}) => this.reactionListItemsBuilder.buildItems$(reactions, config.subject?.apiModel).pipe(
        map((items) => ({items, hasMore, loadedPageCount}))
      ))
    );
  }

  public mergeItems(existingItems: ReactionListItemInterface[], incomingItems: ReactionListItemInterface[]): ReactionListItemInterface[] {
    return this.reactionListItemsBuilder.mergeItems(existingItems, incomingItems);
  }

  public trackBy(index: number, item: ReactionListItemInterface): string {
    return item?.author?.['@id'] || `${index}`;
  }

  private createFetchPage(
    config: SocialListModalConfig<any, ReactionApiModel>,
    page: number,
    pageSize: number
  ): (() => Observable<ReactionApiModel[]>) | null {
    const subjectIri = String(config.subject?.['@id'] || '');
    if (!subjectIri.includes('/posts/')) {
      return null;
    }

    return () => {
      const params = new QueryParamsApiModel();
      params.page = page;
      params.filters.push(new FilterParameterApiModel('limit', pageSize, 'single'));
      params.filters.push(new FilterParameterApiModel('post', subjectIri, 'single'));

      return this.reactionRS.getCollection(params).pipe(
        map((result) => result.records || [])
      );
    };
  }
}
